'use client';
import { Box, Link, useTheme, Typography, IconButton, Fade, Slide, Dialog } from '@mui/material';
import React from 'react';

import { Icon } from '@widget/toolbox/components';
import { Navigation } from './Navigation';
import { Logo } from '@toolbox/components/HeaderCommon/Logo';
import { getParsedNamedSpacing } from '@toolbox/style/theme/common/spacings';

import type { TransitionProps } from '@mui/material/transitions';
import type { HeaderProps } from './Header';

export type SidebarProps = Pick<HeaderProps, 'logos' | 'phoneCTA' | 'navItems'> & {
  toggleSidebar: () => void;
  showSidebar: boolean;
};

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="down" ref={ref} {...props} />;
});

export const Sidebar: React.FC<SidebarProps> = ({
  showSidebar,
  logos,
  toggleSidebar,
  navItems,
  phoneCTA,
}) => {
  const theme = useTheme();
  const defaultStyles = {
    backgroundColor: theme.palette.background.default,
    color: theme.palette.primary.main,
  };
  const primaryStyles = {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  };

  return (
    <Dialog
      id="sidebar"
      open={showSidebar}
      TransitionComponent={Transition}
      fullScreen
      style={{ zIndex: 9999 }}
    >
      <Box display="flex" flexDirection="column" flex={'1 1 auto'}>
        <Box
          sx={defaultStyles}
          flex="0 0 auto"
          py={0}
          pl={2}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          height={{
            xs: getParsedNamedSpacing('headerHeightXs'),
            sm: getParsedNamedSpacing('headerHeightSm'),
            md: getParsedNamedSpacing('headerHeightMd'),
            lg: getParsedNamedSpacing('headerHeightLg'),
            xl: getParsedNamedSpacing('headerHeightXl'),
          }}
        >
          <Box display="flex">
            {logos?.length && logos.map((logo) => <Logo logo={logo} key={logo.image.filename} />)}
          </Box>
          <Box>
            <IconButton disableRipple aria-label="close sidebar" onClick={toggleSidebar}>
              <Icon size={'inherit'} type="close" htmlColor={theme.palette.primary.main} />
            </IconButton>
          </Box>
        </Box>

        <Box sx={primaryStyles} flex="1 1 auto">
          <Navigation navItems={navItems} isMobileNavigation={true} />

          {phoneCTA && (
            <Box p={2}>
              <Link
                href={`tel:${phoneCTA.target}`}
                underline="none"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                }}
                color={'rgb(255, 227, 61)'}
              >
                <Typography color={'inherit'} noWrap={true} mr={2}>
                  {phoneCTA.text}
                </Typography>
                <Box>{phoneCTA.tel}</Box>
              </Link>
            </Box>
          )}
        </Box>
      </Box>
    </Dialog>
  );
};
